/**
 * VaultFilters component - steampunk-styled filtering controls for vault items.
 * Features a mechanical device aesthetic with brass/copper styling, gears, rivets,
 * and a green crystal indicator that glows when filters are active.
 */

import { useMemo } from 'react';
import type { Item, ItemRarityValue } from '@/types/items';
import { STAT_KEYS, SKILL_KEYS, STAT_LABELS, SKILL_LABELS } from '@/types/characters';

/** All rarity options for the filter */
const RARITY_OPTIONS: { value: ItemRarityValue; label: string }[] = [
    { value: 'mundane', label: 'Mundane' },
    { value: 'common', label: 'Common' },
    { value: 'uncommon', label: 'Uncommon' },
    { value: 'rare', label: 'Rare' },
    { value: 'epic', label: 'Epic' },
    { value: 'legendary', label: 'Legendary' },
    { value: 'artifact', label: 'Artifact' },
    { value: 'unique', label: 'Unique' },
];

/** Star rating options (1-5) */
const STAR_OPTIONS = [1, 2, 3, 4, 5];

/** Blueprint filter options */
export type BlueprintFilter = 'all' | 'blueprints' | 'items';

export interface VaultFiltersState {
    search: string;
    rarity: ItemRarityValue | '';
    stars: number | '';
    blueprint: BlueprintFilter;
    statOrSkill: string;
}

interface VaultFiltersProps {
    /** Current filter state */
    filters: VaultFiltersState;
    /** Callback when filters change */
    onFiltersChange: (filters: VaultFiltersState) => void;
    /** Total count of items before filtering */
    totalCount: number;
    /** Count of items after filtering */
    filteredCount: number;
}

/**
 * Get all stat and skill options for the dropdown.
 */
function getStatSkillOptions(): { value: string; label: string; group: 'Stats' | 'Skills' }[] {
    const options: { value: string; label: string; group: 'Stats' | 'Skills' }[] = [];

    // Add stats
    for (const key of STAT_KEYS) {
        options.push({
            value: key,
            label: STAT_LABELS[key],
            group: 'Stats',
        });
    }

    // Add skills
    for (const key of SKILL_KEYS) {
        options.push({
            value: key,
            label: SKILL_LABELS[key],
            group: 'Skills',
        });
    }

    return options;
}

/**
 * Decorative gear SVG component.
 */
function Gear({ size = 24, className = '' }: { size?: number; className?: string }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            className={className}
            fill="currentColor"
        >
            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
            <path
                fillRule="evenodd"
                d="M10.5 1.5a1.5 1.5 0 00-1.5 1.5v.836a8.97 8.97 0 00-1.74.712l-.591-.591a1.5 1.5 0 00-2.122 0L3.043 5.461a1.5 1.5 0 000 2.122l.591.591a8.97 8.97 0 00-.712 1.74H2.086a1.5 1.5 0 00-1.5 1.5v1.172a1.5 1.5 0 001.5 1.5h.836a8.97 8.97 0 00.712 1.74l-.591.591a1.5 1.5 0 000 2.122l1.504 1.504a1.5 1.5 0 002.122 0l.591-.591a8.97 8.97 0 001.74.712v.836a1.5 1.5 0 001.5 1.5h1.172a1.5 1.5 0 001.5-1.5v-.836a8.97 8.97 0 001.74-.712l.591.591a1.5 1.5 0 002.122 0l1.504-1.504a1.5 1.5 0 000-2.122l-.591-.591a8.97 8.97 0 00.712-1.74h.836a1.5 1.5 0 001.5-1.5v-1.172a1.5 1.5 0 00-1.5-1.5h-.836a8.97 8.97 0 00-.712-1.74l.591-.591a1.5 1.5 0 000-2.122l-1.504-1.504a1.5 1.5 0 00-2.122 0l-.591.591a8.97 8.97 0 00-1.74-.712V3a1.5 1.5 0 00-1.5-1.5H10.5zM12 17a5 5 0 100-10 5 5 0 000 10z"
                clipRule="evenodd"
            />
        </svg>
    );
}

/**
 * Decorative rivet component.
 */
function Rivet({ className = '' }: { className?: string }) {
    return (
        <div
            className={`w-3 h-3 rounded-full ${className}`}
            style={{
                background: 'radial-gradient(circle at 30% 30%, #D4A574 0%, #8B6914 50%, #5D4E37 100%)',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.4), 0 1px 2px rgba(0,0,0,0.4)',
            }}
        />
    );
}

/**
 * Green crystal indicator component - positioned absolutely on the right edge of the device.
 */
function CrystalIndicator({ active }: { active: boolean }) {
    return (
        <>
            {/* Crystal housing/mount - attached to right edge */}
            <div
                className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-20 rounded-r-lg z-10"
                style={{
                    background: 'linear-gradient(180deg, #5D4E37 0%, #3D3225 50%, #2A231A 100%)',
                    boxShadow: '2px 0 6px rgba(0,0,0,0.4), inset -2px 0 4px rgba(0,0,0,0.3)',
                    border: '3px solid #8B6914',
                    borderLeft: 'none',
                }}
            >
                {/* Mount rivets */}
                <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full" style={{ background: 'radial-gradient(circle at 30% 30%, #B8860B, #5D4E37)' }} />
                <div className="absolute bottom-2 right-2 w-2.5 h-2.5 rounded-full" style={{ background: 'radial-gradient(circle at 30% 30%, #B8860B, #5D4E37)' }} />

                {/* Crystal centered in housing */}
                <div
                    className="absolute left-1 top-1/2 -translate-y-1/2"
                    style={{
                        width: '24px',
                        height: '48px',
                    }}
                >
                    {/* Crystal shape */}
                    <div
                        className="relative w-full h-full"
                        style={{
                            background: active
                                ? 'linear-gradient(135deg, #7CFC00 0%, #32CD32 30%, #228B22 60%, #006400 100%)'
                                : 'linear-gradient(135deg, #4A5D4A 0%, #3D4D3D 30%, #2D3D2D 60%, #1D2D1D 100%)',
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                            boxShadow: active
                                ? '0 0 20px rgba(50, 205, 50, 0.8), 0 0 40px rgba(50, 205, 50, 0.4), inset 0 0 15px rgba(255,255,255,0.3)'
                                : 'inset 0 0 10px rgba(0,0,0,0.5)',
                            transition: 'all 0.3s ease',
                            animation: active ? 'crystalPulse 2s ease-in-out infinite' : 'none',
                        }}
                    >
                        {/* Inner glow/reflection */}
                        <div
                            className="absolute inset-0"
                            style={{
                                background: active
                                    ? 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)'
                                    : 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                            }}
                        />
                    </div>

                    {/* Glow effect behind crystal when active */}
                    {active && (
                        <div
                            className="absolute inset-0 -z-10"
                            style={{
                                background: 'radial-gradient(circle, rgba(50, 205, 50, 0.6) 0%, rgba(50, 205, 50, 0.3) 40%, transparent 70%)',
                                filter: 'blur(8px)',
                                animation: 'crystalGlow 2s ease-in-out infinite',
                            }}
                        />
                    )}
                </div>
            </div>

            {/* CSS keyframes for animations */}
            <style>{`
                @keyframes crystalPulse {
                    0%, 100% {
                        box-shadow: 0 0 20px rgba(50, 205, 50, 0.8), 0 0 40px rgba(50, 205, 50, 0.4), inset 0 0 15px rgba(255,255,255,0.3);
                    }
                    50% {
                        box-shadow: 0 0 30px rgba(50, 205, 50, 1), 0 0 60px rgba(50, 205, 50, 0.6), inset 0 0 20px rgba(255,255,255,0.5);
                    }
                }
                @keyframes crystalGlow {
                    0%, 100% {
                        opacity: 0.8;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1.2);
                    }
                }
                @keyframes gearSpin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </>
    );
}

/**
 * Steampunk-styled select dropdown.
 */
function SteampunkSelect({
    value,
    onChange,
    children,
    className = '',
}: {
    value: string | number;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={`relative ${className}`}>
            {/* Dial/gauge frame */}
            <div
                className="absolute inset-0 rounded pointer-events-none"
                style={{
                    background: 'linear-gradient(180deg, #8B6914 0%, #6B4423 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 2px 4px rgba(0,0,0,0.3)',
                }}
            />
            <select
                value={value}
                onChange={onChange}
                className="relative w-full px-3 py-2 rounded text-sm font-medium cursor-pointer appearance-none"
                style={{
                    background: 'linear-gradient(180deg, #3D3225 0%, #2A231A 100%)',
                    border: '2px solid #8B6914',
                    color: '#D4A574',
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.4)',
                }}
            >
                {children}
            </select>
            {/* Dropdown arrow styled as a mechanical indicator */}
            <div
                className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: '#B8860B' }}
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M2 4l4 4 4-4H2z" />
                </svg>
            </div>
        </div>
    );
}

/**
 * Steampunk-styled text input.
 */
function SteampunkInput({
    value,
    onChange,
    placeholder,
    className = '',
}: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    className?: string;
}) {
    return (
        <div className={`relative ${className}`}>
            {/* Input frame */}
            <input
                type="text"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="w-full px-3 py-2 rounded text-sm font-medium"
                style={{
                    background: 'linear-gradient(180deg, #2A231A 0%, #1F1A14 100%)',
                    border: '2px solid #8B6914',
                    color: '#D4A574',
                    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)',
                }}
            />
            {/* Search lens icon */}
            <div
                className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: '#8B6914' }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
            </div>
        </div>
    );
}

/**
 * Steampunk-styled button.
 */
function SteampunkButton({
    onClick,
    disabled,
    children,
}: {
    onClick: () => void;
    disabled: boolean;
    children: React.ReactNode;
}) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className="relative px-4 py-2 rounded text-sm font-bold transition-all"
            style={{
                background: disabled
                    ? 'linear-gradient(180deg, #3D3225 0%, #2A231A 100%)'
                    : 'linear-gradient(180deg, #8B6914 0%, #6B4423 50%, #5D3A1A 100%)',
                border: `2px solid ${disabled ? '#5D4E37' : '#B8860B'}`,
                color: disabled ? '#5D4E37' : '#F5DEB3',
                boxShadow: disabled
                    ? 'none'
                    : '0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                cursor: disabled ? 'not-allowed' : 'pointer',
            }}
        >
            {/* Button surface texture */}
            {!disabled && (
                <div
                    className="absolute inset-0 rounded opacity-20 pointer-events-none"
                    style={{
                        backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
                    }}
                />
            )}
            <span className="relative">{children}</span>
        </button>
    );
}

/**
 * VaultFilters component - displays steampunk-styled filter controls for vault items.
 */
export function VaultFilters({ filters, onFiltersChange, totalCount, filteredCount }: VaultFiltersProps) {
    const statSkillOptions = useMemo(() => getStatSkillOptions(), []);

    const hasActiveFilters = filters.search !== '' ||
        filters.rarity !== '' ||
        filters.stars !== '' ||
        filters.blueprint !== 'all' ||
        filters.statOrSkill !== '';

    const handleClearFilters = () => {
        onFiltersChange({
            search: '',
            rarity: '',
            stars: '',
            blueprint: 'all',
            statOrSkill: '',
        });
    };

    return (
        <div>
            {/* Main device wrapper - contains the filter panel and crystal, keeps crystal centered on just this part */}
            <div className="relative pr-10">
                {/* Device label plate - positioned above the container */}
                <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded z-20"
                    style={{
                        background: 'linear-gradient(180deg, #B8860B 0%, #8B6914 100%)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
                        border: '2px solid #6B4423',
                    }}
                >
                    <span
                        className="text-xs font-bold tracking-wider uppercase"
                        style={{ color: '#1F1A14' }}
                    >
                        Item Filtration Device
                    </span>
                </div>

                {/* Main steampunk device container */}
                <div
                    className="relative rounded-lg overflow-hidden"
                style={{
                    background: 'linear-gradient(145deg, #5D4E37 0%, #3D3225 30%, #2A231A 70%, #1F1A14 100%)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
                    border: '3px solid #8B6914',
                }}
            >
                {/* Top brass plate */}
                <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{
                        background: 'linear-gradient(90deg, #B8860B, #D4A574, #B8860B)',
                    }}
                />

                {/* Metal plate texture */}
                <div
                    className="absolute inset-0 opacity-10 pointer-events-none"
                    style={{
                        backgroundImage: `
                            repeating-linear-gradient(90deg, transparent 0px, transparent 3px, rgba(0,0,0,0.1) 3px, rgba(0,0,0,0.1) 4px),
                            repeating-linear-gradient(0deg, transparent 0px, transparent 3px, rgba(0,0,0,0.1) 3px, rgba(0,0,0,0.1) 4px)
                        `,
                    }}
                />

                {/* Decorative gears */}
                <div className="absolute -top-2 -left-2 opacity-60" style={{ color: '#8B6914' }}>
                    <div style={{ animation: 'gearSpin 10s linear infinite' }}>
                        <Gear size={32} />
                    </div>
                </div>
                <div className="absolute -bottom-1 left-16 opacity-40" style={{ color: '#6B4423' }}>
                    <div style={{ animation: 'gearSpin 15s linear infinite reverse' }}>
                        <Gear size={24} />
                    </div>
                </div>
                <div className="absolute top-4 right-4 opacity-30" style={{ color: '#B8860B' }}>
                    <div style={{ animation: 'gearSpin 8s linear infinite' }}>
                        <Gear size={20} />
                    </div>
                </div>

                {/* Corner rivets */}
                <Rivet className="absolute top-2 left-8" />
                <Rivet className="absolute top-2 right-4" />
                <Rivet className="absolute bottom-2 left-8" />
                <Rivet className="absolute bottom-2 right-4" />

                {/* Filter controls content */}
                <div className="relative p-4 pt-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Name Search */}
                        <div className="flex-1 min-w-[180px]">
                            <label
                                className="block text-xs font-bold uppercase tracking-wider mb-1"
                                style={{ color: '#B8860B' }}
                            >
                                Search Lens
                            </label>
                            <SteampunkInput
                                value={filters.search}
                                onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                                placeholder="Item name..."
                            />
                        </div>

                        {/* Rarity Filter */}
                        <div className="w-36">
                            <label
                                className="block text-xs font-bold uppercase tracking-wider mb-1"
                                style={{ color: '#B8860B' }}
                            >
                                Rarity Dial
                            </label>
                            <SteampunkSelect
                                value={filters.rarity}
                                onChange={(e) => onFiltersChange({ ...filters, rarity: e.target.value as ItemRarityValue | '' })}
                            >
                                <option value="">All Rarities</option>
                                {RARITY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </SteampunkSelect>
                        </div>

                        {/* Stars Filter */}
                        <div className="w-28">
                            <label
                                className="block text-xs font-bold uppercase tracking-wider mb-1"
                                style={{ color: '#B8860B' }}
                            >
                                Star Gauge
                            </label>
                            <SteampunkSelect
                                value={filters.stars === '' ? '' : filters.stars.toString()}
                                onChange={(e) => onFiltersChange({ ...filters, stars: e.target.value ? parseInt(e.target.value) : '' })}
                            >
                                <option value="">All Stars</option>
                                {STAR_OPTIONS.map((star) => (
                                    <option key={star} value={star}>
                                        {'★'.repeat(star)}
                                    </option>
                                ))}
                            </SteampunkSelect>
                        </div>

                        {/* Blueprint Filter */}
                        <div className="w-32">
                            <label
                                className="block text-xs font-bold uppercase tracking-wider mb-1"
                                style={{ color: '#B8860B' }}
                            >
                                Type Switch
                            </label>
                            <SteampunkSelect
                                value={filters.blueprint}
                                onChange={(e) => onFiltersChange({ ...filters, blueprint: e.target.value as BlueprintFilter })}
                            >
                                <option value="all">All Types</option>
                                <option value="items">Items Only</option>
                                <option value="blueprints">Blueprints Only</option>
                            </SteampunkSelect>
                        </div>

                        {/* Stat/Skill Filter */}
                        <div className="w-40">
                            <label
                                className="block text-xs font-bold uppercase tracking-wider mb-1"
                                style={{ color: '#B8860B' }}
                            >
                                Stat Selector
                            </label>
                            <SteampunkSelect
                                value={filters.statOrSkill}
                                onChange={(e) => onFiltersChange({ ...filters, statOrSkill: e.target.value })}
                            >
                                <option value="">All Stats/Skills</option>
                                <optgroup label="Stats">
                                    {statSkillOptions.filter(o => o.group === 'Stats').map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label="Skills">
                                    {statSkillOptions.filter(o => o.group === 'Skills').map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </optgroup>
                            </SteampunkSelect>
                        </div>

                        {/* Clear Button */}
                        <div>
                            <SteampunkButton onClick={handleClearFilters} disabled={!hasActiveFilters}>
                                Reset
                            </SteampunkButton>
                        </div>
                    </div>
                </div>

                {/* Bottom brass plate */}
                <div
                    className="absolute bottom-0 left-0 right-0 h-1"
                    style={{
                        background: 'linear-gradient(90deg, #6B4423, #8B6914, #6B4423)',
                    }}
                />
            </div>

                {/* Green Crystal Indicator - positioned to protrude from right side of the wrapper */}
                <CrystalIndicator active={hasActiveFilters} />
            </div>

            {/* Results Drawer - slides out from bottom center when filters are active (outside the crystal wrapper) */}
            {hasActiveFilters && (
                <div className="flex justify-center">
                    <div
                        className="relative -mt-1 px-6 py-3 rounded-b-lg z-10"
                        style={{
                            background: 'linear-gradient(180deg, #3D3225 0%, #2A231A 50%, #1F1A14 100%)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(139, 105, 20, 0.2)',
                            border: '3px solid #8B6914',
                            borderTop: 'none',
                        }}
                    >
                        {/* Drawer side rivets */}
                        <div
                            className="absolute top-2 left-2 w-2 h-2 rounded-full"
                            style={{ background: 'radial-gradient(circle at 30% 30%, #B8860B, #5D4E37)' }}
                        />
                        <div
                            className="absolute top-2 right-2 w-2 h-2 rounded-full"
                            style={{ background: 'radial-gradient(circle at 30% 30%, #B8860B, #5D4E37)' }}
                        />

                        {/* Metal texture */}
                        <div
                            className="absolute inset-0 opacity-10 pointer-events-none rounded-b-lg"
                            style={{
                                backgroundImage: `repeating-linear-gradient(
                                    0deg,
                                    transparent 0px,
                                    transparent 2px,
                                    rgba(0,0,0,0.1) 2px,
                                    rgba(0,0,0,0.1) 3px
                                )`,
                            }}
                        />

                        {/* Counter display */}
                        <div className="relative flex items-center gap-2">
                            <div
                                className="px-3 py-1 rounded"
                                style={{
                                    background: 'linear-gradient(180deg, #1F1A14 0%, #0F0D0A 100%)',
                                    border: '2px solid #5D4E37',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                                }}
                            >
                                <span className="font-mono text-lg font-bold" style={{ color: '#7CFC00' }}>
                                    {filteredCount}
                                </span>
                            </div>
                            <span className="text-sm font-medium" style={{ color: '#8B7355' }}>
                                of
                            </span>
                            <div
                                className="px-3 py-1 rounded"
                                style={{
                                    background: 'linear-gradient(180deg, #1F1A14 0%, #0F0D0A 100%)',
                                    border: '2px solid #5D4E37',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                                }}
                            >
                                <span className="font-mono text-lg font-bold" style={{ color: '#D4A574' }}>
                                    {totalCount}
                                </span>
                            </div>
                            <span className="text-sm font-medium" style={{ color: '#8B7355' }}>
                                items detected
                            </span>
                        </div>

                        {/* Drawer handle */}
                        <div className="flex justify-center mt-2">
                            <div
                                className="w-12 h-1.5 rounded-full"
                                style={{
                                    background: 'linear-gradient(180deg, #6B4423 0%, #8B6914 50%, #6B4423 100%)',
                                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)',
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Filter vault items based on the current filter state.
 */
export function filterVaultItems(items: Item[], filters: VaultFiltersState): Item[] {
    return items.filter((item) => {
        // Search filter (partial match on name)
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            if (!item.name.toLowerCase().includes(searchLower)) {
                return false;
            }
        }

        // Rarity filter
        if (filters.rarity && item.rarity.value !== filters.rarity) {
            return false;
        }

        // Stars filter
        if (filters.stars !== '' && item.quality !== filters.stars) {
            return false;
        }

        // Blueprint filter
        if (filters.blueprint === 'blueprints' && !item.is_blueprint) {
            return false;
        }
        if (filters.blueprint === 'items' && item.is_blueprint) {
            return false;
        }

        // Stat/Skill filter - check if item has this stat or skill
        if (filters.statOrSkill) {
            const hasStatOrSkill = Object.keys(item.stats).includes(filters.statOrSkill) ||
                (item.recipe_fixed_stats && Object.keys(item.recipe_fixed_stats).includes(filters.statOrSkill)) ||
                (item.recipe_allowed_stats && Object.keys(item.recipe_allowed_stats).includes(filters.statOrSkill));

            if (!hasStatOrSkill) {
                return false;
            }
        }

        return true;
    });
}

/**
 * Get the default/empty filter state.
 */
export function getDefaultFilters(): VaultFiltersState {
    return {
        search: '',
        rarity: '',
        stars: '',
        blueprint: 'all',
        statOrSkill: '',
    };
}

/**
 * Parse filters from URL query parameters.
 */
export function parseFiltersFromUrl(): VaultFiltersState {
    if (typeof window === 'undefined') return getDefaultFilters();

    const params = new URLSearchParams(window.location.search);

    return {
        search: params.get('search') || '',
        rarity: (params.get('rarity') as ItemRarityValue) || '',
        stars: params.get('stars') ? parseInt(params.get('stars')!) : '',
        blueprint: (params.get('type') as BlueprintFilter) || 'all',
        statOrSkill: params.get('stat') || '',
    };
}

/**
 * Update URL query parameters with current filters.
 * Preserves the tab parameter.
 */
export function updateUrlWithFilters(filters: VaultFiltersState): void {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);

    // Set or remove filter params
    if (filters.search) {
        url.searchParams.set('search', filters.search);
    } else {
        url.searchParams.delete('search');
    }

    if (filters.rarity) {
        url.searchParams.set('rarity', filters.rarity);
    } else {
        url.searchParams.delete('rarity');
    }

    if (filters.stars !== '') {
        url.searchParams.set('stars', filters.stars.toString());
    } else {
        url.searchParams.delete('stars');
    }

    if (filters.blueprint !== 'all') {
        url.searchParams.set('type', filters.blueprint);
    } else {
        url.searchParams.delete('type');
    }

    if (filters.statOrSkill) {
        url.searchParams.set('stat', filters.statOrSkill);
    } else {
        url.searchParams.delete('stat');
    }

    // Update URL without adding to history (just replace current state)
    window.history.replaceState({}, '', url.toString());
}

export default VaultFilters;
